import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { Capacitor } from '@capacitor/core';
import { App as NativeApp } from '@capacitor/app';
import { SERVER_URL } from '../config/server.js';

const SESSION_KEY = 'capitalia-session-v1';
const MUTATING_EVENTS = new Set([
  'room:start',
  'room:bots',
  'game:roll',
  'game:end-turn',
  'property:buy',
  'property:decline',
  'property:build',
  'property:sell-building',
  'property:mortgage',
  'property:unmortgage',
  'jail:pay',
  'jail:use-card',
  'debt:pay',
  'bankruptcy:declare',
  'trade:create',
  'trade:accept',
  'trade:decline',
]);

export function readSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY)) ?? null;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export function useGameSocket({ resumeSavedSession = false } = {}) {
  const socketRef = useRef(null);
  const resumeAttemptRef = useRef(false);
  const stateRef = useRef(null);
  const [state, setState] = useState(null);
  const [session, setSession] = useState(() => resumeSavedSession ? readSession() : null);
  const boundSessionRef = useRef(session);
  const pendingEntryRef = useRef(null);
  const snapshotEpochRef = useRef(0);
  const [connected, setConnected] = useState(false);
  const [booting, setBooting] = useState(Boolean(session));
  const [error, setError] = useState(null);
  const [connectionFailed, setConnectionFailed] = useState(false);

  if (socketRef.current == null) {
    socketRef.current = io(SERVER_URL, {
      autoConnect: false,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 4_000,
    });
  }

  const installState = useCallback((nextState, resync = false) => {
    const saved = boundSessionRef.current;
    if (!saved || nextState?.code !== saved.roomCode || nextState.viewerId !== saved.playerId) return;
    const previous = stateRef.current;
    const latest = previous?.code === nextState.code && previous.game.id === nextState.game.id
      && previous.game.revision > nextState.game.revision ? previous : nextState;
    if (resync) snapshotEpochRef.current += 1;
    const installed = { ...latest, snapshotEpoch: snapshotEpochRef.current };
    // The next action can run before React renders this authoritative snapshot.
    stateRef.current = installed;
    setState(installed);
    localStorage.setItem(SESSION_KEY, JSON.stringify({ ...saved, status: installed.game.status, updatedAt: Date.now() }));
  }, []);

  const emit = useCallback((event, payload = {}) => new Promise((resolve, reject) => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      const offlineError = new Error('Нет соединения с сервером. Подождите переподключения.');
      setError(offlineError.message);
      reject(offlineError);
      return;
    }
    const requestPayload = MUTATING_EVENTS.has(event)
      ? {
        ...payload,
        // getRandomValues is available on HTTP LAN origins as well as HTTPS.
        actionId: payload.actionId ?? Array.from(
          crypto.getRandomValues(new Uint8Array(16)),
          (byte) => byte.toString(16).padStart(2, '0'),
        ).join(''),
        expectedRevision: payload.expectedRevision ?? stateRef.current?.game?.revision,
      }
      : payload;
    socket.timeout(8_000).emit(event, requestPayload, (timeoutError, response) => {
      if (timeoutError) {
        const requestError = new Error('Сервер не ответил вовремя. Попробуйте ещё раз.');
        setError(requestError.message);
        reject(requestError);
        return;
      }
      if (!response?.ok) {
        if (response?.error?.code === 'STALE_ACTION' && response.state) installState(response.state, true);
        const actionError = new Error(response?.error?.message ?? 'Действие не выполнено.');
        actionError.code = response?.error?.code;
        setError(actionError.message);
        reject(actionError);
        return;
      }
      setError(null);
      resolve(response);
    });
  }), [installState]);

  useEffect(() => {
    const socket = socketRef.current;
    const connectionTimer = setTimeout(() => {
      if (!socket.connected) { setBooting(false); setConnectionFailed(true); }
    }, 8_000);
    const handleConnect = async () => {
      clearTimeout(connectionTimer);
      setConnectionFailed(false);
      setConnected(true);
      // A fresh lobby is unbound. Only explicit Continue or this socket's room
      // binding may resume; unrelated saved credentials must stay untouched.
      const saved = boundSessionRef.current;
      if (!saved || resumeAttemptRef.current) {
        setBooting(false);
        return;
      }
      resumeAttemptRef.current = true;
      try {
        const response = await emit('room:resume', saved);
        boundSessionRef.current = response.session;
        setSession(response.session);
        installState(response.state, true);
      } catch (resumeError) {
        if (['ROOM_NOT_FOUND', 'SESSION_NOT_FOUND', 'INVALID_SESSION'].includes(resumeError.code)) {
          localStorage.removeItem(SESSION_KEY);
          boundSessionRef.current = null;
          setSession(null);
        }
      } finally {
        setBooting(false);
      }
    };
    const handleDisconnect = () => {
      setConnected(false);
      resumeAttemptRef.current = false;
    };
    const handleState = (nextState) => {
      const pending = pendingEntryRef.current;
      if (pending) {
        // Server broadcasts entry before its acknowledgement. Retain the newest
        // candidate until the acknowledgement establishes the room and player.
        if (!pending.roomCode || pending.roomCode === nextState.code) pending.states.set(nextState.code, nextState);
        return;
      }
      installState(nextState);
    };
    const handleActionError = (actionError) => setError(actionError.message);

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('state:update', handleState);
    socket.on('action:error', handleActionError);
    socket.connect();

    const lifecycleListener = Capacitor.isNativePlatform()
      ? NativeApp.addListener('appStateChange', ({ isActive }) => {
        // Re-bind the saved room session and request fresh authoritative state.
        if (isActive) socket.connect();
        else socket.disconnect();
      })
      : null;

    return () => {
      clearTimeout(connectionTimer);
      lifecycleListener?.then((listener) => listener.remove());
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('state:update', handleState);
      socket.off('action:error', handleActionError);
      socket.disconnect();
    };
  }, [emit, installState]);

  const enterRoom = useCallback(async (event, payload) => {
    const pending = { roomCode: payload.roomCode, states: new Map() };
    pendingEntryRef.current = pending;
    try {
      const response = await emit(event, payload);
      boundSessionRef.current = response.session;
      setSession(response.session);
      installState(response.state, true);
      const broadcast = pending.states.get(response.session.roomCode);
      if (broadcast) installState(broadcast);
      return response;
    } finally {
      if (pendingEntryRef.current === pending) pendingEntryRef.current = null;
    }
  }, [emit, installState]);

  const leaveLocalSession = useCallback(async () => {
    // A failed leave keeps the room binding and credentials available for retry.
    await emit('room:leave');
    localStorage.removeItem(SESSION_KEY);
    boundSessionRef.current = null;
    pendingEntryRef.current = null;
    setSession(null);
    stateRef.current = null;
    setState(null);
    setError(null);
    resumeAttemptRef.current = false;
    socketRef.current.disconnect();
    socketRef.current.connect();
  }, [emit]);

  return {
    state,
    session,
    connected,
    booting,
    error,
    connectionFailed,
    clearError: () => setError(null),
    createRoom: (payload) => enterRoom('room:create', payload),
    joinRoom: (payload) => enterRoom('room:join', payload),
    action: emit,
    leaveLocalSession,
  };
}
